// Small fetch helper that survives transient Cloudflare 502/503/504s.
// Used everywhere a 5xx blip would otherwise show "Couldn't load…"
import axios from "axios";

const DEFAULT_RETRIES = 3;
const RETRY_DELAYS_MS = [600, 1400, 3000]; // exponential-ish backoff
const RETRYABLE_STATUSES = new Set([502, 503, 504, 408, 429]);

function delay(ms) { return new Promise((r) => setTimeout(r, ms)); }

/**
 * axiosWithRetry — does the right thing for production API calls behind a CDN.
 * Retries on 5xx-class blips, network errors, and 408/429.
 * Does NOT retry on 4xx (except 408/429) — those are real client errors.
 */
export async function axiosWithRetry(config, { retries = DEFAULT_RETRIES, onRetry } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await axios({ timeout: 12000, ...config });
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      const isNetwork = !err?.response; // no response = network/CORS/abort
      const retryable = isNetwork || (status && RETRYABLE_STATUSES.has(status));
      if (!retryable || attempt === retries) break;
      if (onRetry) onRetry(attempt + 1, err);
      await delay(RETRY_DELAYS_MS[attempt] || 3000);
    }
  }
  throw lastErr;
}

// Convenience wrappers — match axios.get / axios.post call signatures.
export function getWithRetry(url, config = {}, opts) {
  return axiosWithRetry({ method: "get", url, ...config }, opts);
}
export function postWithRetry(url, data, config = {}, opts) {
  return axiosWithRetry({ method: "post", url, data, ...config }, opts);
}

/**
 * cachedFetch — for pages where stale-while-revalidate is the right UX.
 * Tries network first; on failure, falls back to the last-known-good
 * response saved in localStorage. Always refreshes the cache on success.
 */
export async function cachedFetch(key, url, config = {}) {
  const cacheKey = `mm:cache:${key}`;
  const ttlKey = `mm:cache:${key}:ts`;
  try {
    const res = await getWithRetry(url, config);
    try {
      localStorage.setItem(cacheKey, JSON.stringify(res.data));
      localStorage.setItem(ttlKey, String(Date.now()));
    } catch { /* localStorage may be full / private mode */ }
    return { data: res.data, fromCache: false };
  } catch (err) {
    // Fall back to cached payload if available
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        return { data: JSON.parse(cached), fromCache: true, err };
      }
    } catch { /* ignore */ }
    throw err;
  }
}
